/**
 * Attendance for schools that take more than one register a day.
 *
 * The overview API sends one roll-up status per day ("Half day" when the child
 * was there for one sitting and not the other) plus, per day, the registers
 * actually taken (`sessions`). These helpers turn that into what the parent
 * reads: "Morning - Present, Afternoon - Absent", with times in the parent's own
 * timezone.
 *
 * "Today" is always the DEVICE's calendar date. The API's own `today` field is
 * picked with the server's UTC clock, which in Cayman (UTC-5) is already
 * tomorrow from 7pm - the screen would say "Not marked yet" every evening.
 */
import {
  AttendanceDayStatus,
  AttendanceMonthDay,
  AttendanceSessionInfo,
  AttendanceSessionStatus,
} from '../types';
import { formatLocalTime, parseBackendDateOnly } from './dateUtils';

export const ATTENDANCE_STATUS_COLOR: Record<AttendanceDayStatus, string> = {
  Present: '#2563EB',
  Leave: '#F59E0B',
  Absent: '#DC2626',
  Late: '#0891B2',
  'Half day': '#F97316',
  Remote: '#06B6D4',
};

/** Feather icon names. */
export const ATTENDANCE_STATUS_ICON: Record<AttendanceDayStatus, string> = {
  Present: 'check-circle',
  Leave: 'calendar',
  Absent: 'x-circle',
  Late: 'clock',
  'Half day': 'pie-chart',
  Remote: 'home',
};

const FULL_DAY = 'FULL_DAY';

/** "2026-10-09" for a Date, in the device's own timezone. */
export function toDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The calendar day an API date names ("2026-10-09T00:00:00" -> "2026-10-09"). */
export function dayKeyOf(date: string): string {
  return String(date ?? '').slice(0, 10);
}

export function findDay<T extends { date: string }>(days: T[] | null | undefined, key: string): T | null {
  return (days ?? []).find(d => dayKeyOf(d.date) === key) ?? null;
}

/** "Thursday, 9 October" for a date key. */
export function formatDayHeading(key: string): string {
  const parsed = parseBackendDateOnly(key);
  return parsed
    ? parsed.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
    : key;
}

/** "Thu, 9 Oct" for a date key. */
export function formatDayShort(key: string): string {
  const parsed = parseBackendDateOnly(key);
  return parsed
    ? parsed.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })
    : key;
}

export interface SessionRow {
  code: string;
  label: string;
  ordinal: number;
  /** null = this register has not been taken yet today. */
  status: AttendanceSessionStatus | null;
  /** "Arrived 8:42 AM", "Marked at 8:15 AM", "Not marked yet", or ''. */
  detail: string;
}

/**
 * One row per register taken on the day, in the order they happen.
 *
 * With `includePending` (today only), once at least one register has been taken,
 * the ones still to come are added as "Not marked yet" - so at lunchtime a
 * parent sees "Morning - Present, Afternoon - Not marked yet" rather than a day
 * that looks finished.
 */
export function buildSessionRows(
  day: AttendanceMonthDay | null | undefined,
  expected: AttendanceSessionInfo[] = [],
  includePending = false,
): SessionRow[] {
  const taken = [...(day?.sessions ?? [])].sort((a, b) => a.ordinal - b.ordinal);

  const rows: SessionRow[] = taken.map(s => ({
    code: s.code,
    label: s.label || 'Register',
    ordinal: s.ordinal,
    status: s.status,
    detail:
      s.status === 'Late' && s.arrivalTime
        ? `Arrived ${formatLocalTime(s.arrivalTime)}`
        : s.markedAt
          ? `Marked at ${formatLocalTime(s.markedAt)}`
          : '',
  }));

  // Pending rows only once the school has started the day's registers: before
  // any is taken we cannot tell a school day from a weekend or holiday, and two
  // "Pending" rows on a Saturday would be wrong. The caller shows a plain
  // "Not marked yet" instead.
  // A leave day is leave all day - nothing left to wait for. Likewise a day taken
  // under the single daily register (FULL_DAY) has no morning/afternoon to add,
  // even if the school has since moved to two registers.
  const isLeaveDay = day?.status === 'Leave';
  const tookSingleRegister = taken.some(s => s.code === FULL_DAY);
  if (includePending && taken.length > 0 && !isLeaveDay && !tookSingleRegister) {
    const takenCodes = new Set(taken.map(s => s.code));
    expected
      .filter(e => !takenCodes.has(e.code))
      .forEach(e =>
        rows.push({
          code: e.code,
          label: e.label || 'Register',
          ordinal: e.ordinal,
          status: null,
          detail: 'Not marked yet',
        }),
      );
  }

  return rows.sort((a, b) => a.ordinal - b.ordinal);
}

/**
 * "At school for 1 of 2 registers" - only for a day with more than one
 * register, where the roll-up alone ("Half day") does not say how much.
 */
export function sittingsSummary(day: AttendanceMonthDay | null | undefined): string {
  const held = day?.sessionsHeld ?? 0;
  if (!day || held < 2 || day.status === 'Leave') return '';
  const attended = Math.max(0, Math.min(day.sessionsAttended ?? held, held));
  return `At school for ${attended} of ${held} registers`;
}

/** "Morning: Present · Afternoon: Absent" - for compact history rows. */
export function sessionsLine(day: AttendanceMonthDay | null | undefined): string {
  const sessions = [...(day?.sessions ?? [])].sort((a, b) => a.ordinal - b.ordinal);
  if (sessions.length < 2) return '';
  return sessions.map(s => `${s.label}: ${s.status}`).join(' · ');
}

/** Recorded days up to and including today, newest first. */
export function historyDays<T extends { date: string }>(days: T[] | null | undefined, todayKey: string): T[] {
  return (days ?? [])
    .filter(d => dayKeyOf(d.date) <= todayKey)
    .sort((a, b) => (dayKeyOf(a.date) < dayKeyOf(b.date) ? 1 : -1));
}

/** Normalises GET /student/attendance/sessions; tolerates PascalCase and missing data. */
export function toSessionInfoList(raw: unknown): AttendanceSessionInfo[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s: any) => ({
      code: String(s?.code ?? s?.Code ?? ''),
      label: String(s?.label ?? s?.Label ?? ''),
      ordinal: Number(s?.ordinal ?? s?.Ordinal ?? 0),
    }))
    .filter(s => s.code)
    .sort((a, b) => a.ordinal - b.ordinal);
}
